'use client'

/**
 * Series tables for /data/price-index — brand-standard table treatment.
 *
 * White card, gray-200 border, rounded-xl, gray-50 uppercase header row,
 * hairline row dividers, right-aligned tabular numerals, and an
 * `overflow-x-auto` scroll container so wide tables never break the page.
 * Below-threshold rows are rendered muted and explicitly flagged — disclosed,
 * never hidden, never presented as published figures.
 *
 * Every value arrives via components/seo/data-index-summary.ts (the single
 * importer of app/data/_data/price-index.json). Server components — no JS.
 */
import type { ReactNode } from 'react'
import {
  type SeriesRow,
  type CategorySummary,
  formatMonthLabel,
  formatDollars,
} from '@/components/seo/data-index-summary'
import { SortableTable, type SortableColumn } from './sortable-table'
import { EntityLink, type LinkTarget } from './entity-link'
import { TableScrollHint } from '@/components/ui/table-scroll-hint'

/**
 * A category summary plus the live collection the page resolved for it
 * (FRIDGE-334) — resolved server-side by lib/data/entity-links.ts, rendered
 * here as a <Link>; null → plain text.
 */
export type LinkedCategorySummary = CategorySummary & { link?: LinkTarget }

/**
 * Frozen header row and frozen first column (FRIDGE-363, owner directive) —
 * the same treatment the sortable league tables carry, for the same reason:
 * each of these cards is a full month-by-month series (26 rows on
 * /data/price-index, taller than a viewport), and an index reading is
 * unreadable once its column heading and its month have both scrolled away.
 * See app/data/_components/sortable-table.tsx for why the container needs an
 * explicit max-height, why the table must be `border-separate`, and why the
 * frozen cells need their own background.
 */
const TH_BASE = 'px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-500 whitespace-nowrap sticky top-0 bg-gray-50 border-b border-gray-200'
const TH = TH_BASE + ' text-left z-20'
const TH_NUM = TH_BASE + ' text-right z-20'
/**
 * The month column is the row label — it stays put while the figures scroll
 * sideways, and its header cell is frozen on BOTH axes so it must outrank the
 * rest of the header row. Exactly one `z-*` per class string: two would leave
 * the winner to Tailwind's stylesheet order rather than to this file.
 */
const TH_FIRST = TH_BASE + ' text-left left-0 z-30 border-r border-gray-200'
const TD = 'px-4 py-2.5 whitespace-nowrap border-t border-gray-100'
const TD_NUM = 'px-4 py-2.5 whitespace-nowrap text-right tabular-nums border-t border-gray-100'
const TD_FIRST = TD + ' sticky left-0 z-10 bg-inherit border-r border-gray-200'

function PublishedPill() {
  return (
    <span className="inline-block rounded-full bg-[#f2f7ff] text-[#0469f0] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
      Published
    </span>
  )
}

function BelowThresholdPill() {
  return (
    <span className="inline-block rounded-full bg-gray-100 text-[#697080] text-[10px] font-bold uppercase tracking-wider px-2 py-0.5">
      Below threshold
    </span>
  )
}

function TableShell({
  children,
  label,
  minWidth = 480,
  maxHeight = '70vh',
}: {
  children: ReactNode
  /**
   * Accessible name for the scrollport (see the keyboard note below). Required
   * rather than optional: a focusable region with no name is announced as an
   * unlabelled group, which trades one defect for another.
   */
  label: string
  minWidth?: number
  maxHeight?: string
}) {
  return (
    // `overflow-auto` + an explicit cap: this box is the scrollport the frozen
    // header resolves against, so without a height it could never pin.
    //
    // KEYBOARD REACHABILITY (axe `scrollable-region-focusable`, serious).
    // Capping the height is what makes this box scroll, and these static series
    // tables hold no button, link or field — so before `tabIndex`, their content
    // below the fold was reachable by mouse wheel and touch only, and not at all
    // by keyboard. `tabIndex={0}` makes the scrollport itself focusable so the
    // arrow keys scroll it; `role="region"` + `aria-label` mean focus landing
    // there announces WHICH table, not "group".
    //
    // Why the sortable sibling needs none of this: every column header in
    // `SortableTable` is a `<button>`, so that scrollport already contains
    // focusable descendants and axe correctly passes it. Adding `tabIndex` there
    // would only add a redundant tab stop. Measured, not assumed — the rule fired
    // on 18 nodes on /data/price-index and every one was this component.
    <>
    <TableScrollHint />
    <div
      className="overflow-auto focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0469f0]"
      style={{ maxHeight }}
      // TWO ACCESSIBILITY GATES DISAGREE HERE, and the rendered-page one wins.
      //
      // `scripts/accessibility-axe-gate.ts` runs axe against a real production
      // build and flagged `scrollable-region-focusable` as SERIOUS on 18 nodes
      // of /data/price-index — every one this element. A region that scrolls but
      // holds no focusable descendant cannot be reached or scrolled by keyboard
      // at all, which is the actual barrier for a real user.
      //
      // `tabIndex={0}` + `role="region"` + `aria-label` is the pattern WAI
      // documents for exactly that, and it is what axe asks for. The static rule
      // `jsx-a11y/no-noninteractive-tabindex` objects because `region` is not an
      // interactive role — it cannot see that the element scrolls, which is the
      // whole reason the attribute is needed.
      //
      // Suppressed on this line only, with the axe measurement as the evidence.
      // Removing the attribute to satisfy the static rule would reintroduce 18
      // serious violations on a live page. Both rules stay fully enforced
      // everywhere else. (FRIDGE-412)
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- axe scrollable-region-focusable, 18 serious nodes; see above
      tabIndex={0}
      role="region"
      aria-label={label}
    >
      <table
        className="w-full text-sm text-gray-900"
        // Inline, not the `border-separate` utility — Tailwind 4 emits no rule
        // for it in this project, and a frozen header silently loses its
        // dividers under `border-collapse: collapse`.
        style={{ minWidth, borderCollapse: 'separate', borderSpacing: 0 }}
      >
        {children}
      </table>
    </div>
    </>
  )
}

/** Latest qualifying month per published category — the headline table (click-to-sort). */
export function LatestReadingsTable({ summaries }: { summaries: LinkedCategorySummary[] }) {
  const columns: SortableColumn<LinkedCategorySummary>[] = [
    {
      key: 'category',
      header: 'Category',
      sortValue: (s) => s.label,
      render: (s) => (
        <EntityLink link={s.link} className="font-semibold">
          {s.label}
        </EntityLink>
      ),
    },
    { key: 'month', header: 'Month', sortValue: (s) => s.latestMonth ?? '', render: (s) => <span className="text-gray-600">{formatMonthLabel(s.latestMonth)}</span> },
    { key: 'index', header: 'Index (base = 100)', numeric: true, sortValue: (s) => s.latestIndex ?? -1, render: (s) => <span className="font-bold">{s.latestIndex === null ? '—' : s.latestIndex.toFixed(2)}</span> },
    { key: 'n', header: 'n', numeric: true, sortValue: (s) => s.latestN, render: (s) => <span className="text-gray-600">{s.latestN}</span> },
    { key: 'median', header: 'Median', numeric: true, sortValue: (s) => s.latestMedianCents ?? -1, render: (s) => <span className="text-gray-600">{formatDollars(s.latestMedianCents)}</span> },
  ]
  return (
    <SortableTable
      columns={columns}
      rows={summaries}
      rowKey={(s) => s.category}
      defaultSortKey="median"
      defaultDir="desc"
      minWidth={640}
      caption="Latest published readings of the Fridge.com Appliance Price Index — sortable | Fridge.com"
    />
  )
}

/**
 * One category's full month-by-month series in a titled card:
 * month · index · n · median · publication status.
 */
/** Columns for the monthly series (FRIDGE-450). Sort keys: month lexically
 *  (ISO YYYY-MM sorts correctly as a string), the numerics numerically with
 *  null Index sorting to the bottom. Muting is row-level via `muted`. */
const SERIES_COLUMNS: SortableColumn<SeriesRow>[] = [
  { key: 'month', header: 'Month', sortValue: (r) => r.month, render: (r) => r.month },
  {
    key: 'index',
    header: 'Index',
    numeric: true,
    sortValue: (r) => r.index ?? -Infinity,
    render: (r) => (r.index === null ? '—' : <span className={r.below_publication_threshold ? '' : 'font-semibold'}>{r.index.toFixed(2)}</span>),
  },
  { key: 'n', header: 'n', numeric: true, sortValue: (r) => r.n, render: (r) => r.n },
  { key: 'median', header: 'Median', numeric: true, sortValue: (r) => r.median_price_cents ?? -Infinity, render: (r) => formatDollars(r.median_price_cents) },
  { key: 'status', header: 'Status', sortValue: (r) => (r.below_publication_threshold ? 1 : 0), render: (r) => (r.below_publication_threshold ? <BelowThresholdPill /> : <PublishedPill />) },
]

export function CategorySeriesCard({
  title,
  metaLine,
  rows,
  link,
}: {
  title: string
  metaLine: string
  rows: SeriesRow[]
  /** Live collection for this category (FRIDGE-334); null → plain title. */
  link?: LinkTarget
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden h-full">
      <div className="px-5 py-4 border-b border-gray-200">
        <h4 className="text-base font-bold tracking-tight text-gray-900">
          <EntityLink link={link}>{title}</EntityLink>
        </h4>
        <p className="mt-0.5 text-[11px] uppercase tracking-[0.12em] text-gray-500">{metaLine}</p>
      </div>
      {/* FRIDGE-450 (owner ruling 08/21/2026: every data element fully
          interactive): the monthly series was the page's only static table
          family — five instances, zero sortable columns. It now rides the
          same SortableTable as the latest-readings table above it, whose
          client chunk is already on the page, so this adds no First Load JS.
          Rows are months, not entities, so cells stay unlinked by design —
          the card TITLE carries the collection link via EntityLink. */}
      <SortableTable<SeriesRow>
        columns={SERIES_COLUMNS}
        rows={rows}
        rowKey={(r) => r.month}
        muted={(r) => r.below_publication_threshold}
        defaultSortKey="month"
        defaultDir="desc"
        minWidth={480}
        caption={`${title} — monthly series`}
      />
    </div>
  )
}
